import { getAccessToken, isAccessTokenExpired, setAccessToken, clearTokens, getRefreshToken } from "@/lib/token-storage";
import { refreshToken } from "./auth";

function baseUrls() {
  // Don't use BACKEND_INTERNAL_URL - always use proxy via relative URLs
  const external = (import.meta as any).env?.BACKEND_EXTERNAL_URL as string | undefined;
  return {
    internal: "", // Always use relative URLs to go through Vite proxy
    external: external || "",
  };
}

function withLeadingSlash(path: string): string {
  if (!path.startsWith("/")) return `/${path}`;
  return path;
}

/**
 * Resolve a backend-provided asset URL for use in <img src="...">.
 *
 * Backend sometimes returns relative paths (e.g. "/media/..."). In production the frontend
 * may be served from a different origin than the backend API, so we need to prefix with
 * the backend origin when available.
 */
export function resolveBackendAssetUrl(url: string | null | undefined): string | null {
  if (!url) return null;

  // Already absolute or non-http schemes we should preserve as-is.
  if (
    url.startsWith("http://") ||
    url.startsWith("https://") ||
    url.startsWith("data:") ||
    url.startsWith("blob:")
  ) {
    return url;
  }

  const path = withLeadingSlash(url);

  // Prefer the same origin used by apiFetch in production.
  const env = (import.meta as any).env as Record<string, string | undefined> | undefined;
  const external = env?.BACKEND_EXTERNAL_URL || env?.VITE_BACKEND_URL || "";
  if (!external) return path;

  return `${external}${path}`;
}

export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const { internal, external } = baseUrls();
  
  // Get access token and check if it needs refresh
  let accessToken = getAccessToken();
  if (!accessToken || isAccessTokenExpired()) {
    console.log("[apiFetch] Access token expired or missing, attempting refresh");
    const newToken = await refreshToken();
    if (newToken) {
      accessToken = newToken;
    } else {
      // If refresh fails, clear tokens and let the request fail with 401
      console.warn("[apiFetch] Token refresh failed, request may fail with 401");
    }
  }

  const opts: RequestInit = {
    ...init,
    headers: {
      ...(init.headers || {}),
    },
  };

  // Add Authorization header if we have an access token
  if (accessToken) {
    (opts.headers as Record<string, string>)["Authorization"] = `Bearer ${accessToken}`;
  }

  const fullPath = withLeadingSlash(path);
  
  // If external backend URL is set (production), use it directly
  // Otherwise, use relative URLs for Vite proxy (development)
  if (external) {
    const externalUrl = external + fullPath;
    console.log(`[apiFetch] ${init.method || "GET"} ${path}`);
    console.log(`[apiFetch] Using external URL (production): "${externalUrl}"`);
    console.log(`[apiFetch] Has access token:`, !!accessToken);
    const res = await fetch(externalUrl, opts);
    
    // If we get 401, try refreshing token once and retry
    if (res.status === 401 && accessToken) {
      console.log("[apiFetch] Got 401, attempting token refresh and retry");
      const newToken = await refreshToken();
      if (newToken) {
        (opts.headers as Record<string, string>)["Authorization"] = `Bearer ${newToken}`;
        return await fetch(externalUrl, opts);
      }
    }
    
    console.log(`[apiFetch] External response status: ${res.status}`);
    return res;
  }
  
  // Development: use relative URL for Vite proxy
  const internalUrl = internal + fullPath;
  console.log(`[apiFetch] ${init.method || "GET"} ${path}`);
  console.log(`[apiFetch] Using relative URL (dev proxy): "${internalUrl}"`);
  console.log(`[apiFetch] Has access token:`, !!accessToken);
  const res = await fetch(internalUrl, opts);
  
  // If we get 401, try refreshing token once and retry
  if (res.status === 401 && accessToken) {
    console.log("[apiFetch] Got 401, attempting token refresh and retry");
    const newToken = await refreshToken();
    if (newToken) {
      (opts.headers as Record<string, string>)["Authorization"] = `Bearer ${newToken}`;
      return await fetch(internalUrl, opts);
    }
  }
  
  console.log(`[apiFetch] Proxy response status: ${res.status}`);
  return res;
}


